import React from 'react';
import { Button } from '@/components/ui/button';
import { getFilteredMainNavItems, mainNavItems } from './navigationConfig';
import { useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { TooltipProvider } from '@/components/ui/tooltip-provider';
import { cn } from '@/lib/utils';
import { useAppSelector } from '@/hooks/useAppSelector';
import { selectAuthLoading, selectIsAuthenticated } from '@/store/selectors/authSelectors';
import Protect from '../auth/Protect';
import { getPermissionForNavigation } from '@/utils/betterAuthPermissionMapper';

interface MainNavigationProps {
  activeMainNav: string;
  setActiveMainNav: (nav: string) => void;
  navigate: (path: string) => void;
}

const MainNavigation: React.FC<MainNavigationProps> = ({
  activeMainNav,
  setActiveMainNav,
  navigate
}) => {
  const location = useLocation();
  const authLoading = useAppSelector(selectAuthLoading);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.altKey) {
        mainNavItems.forEach((item, index) => {
          if (event.key === `${index + 1}`) {
            setActiveMainNav(item.id);
            const hasSubNav = item.id !== 'home';
            if (!hasSubNav) {
              navigate(item.path);
            }
          }
        });
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [navigate, setActiveMainNav]);

  // Enhanced filtering using CASL permissions only
  // const accessibleNavItems = React.useMemo(() => {
  //   console.log('🧭 Filtering main navigation items using CASL permissions...');

  //   // During auth loading, show basic navigation to prevent icons from disappearing
  //   if (authLoading && isAuthenticated) {
  //     console.log('⏳ Auth loading - showing basic navigation during loading');
  //     return mainNavItems.filter(item => ['home', 'inbox'].includes(item.id));
  //   }

  //   // If no ability available, show basic items
  //   if (!ability) {
  //     console.log('⚠️ No ability available - showing basic navigation');
  //     return mainNavItems.filter(item => ['home', 'inbox'].includes(item.id));
  //   }

  //   // Filter based on CASL permissions only
  //   const filtered = mainNavItems.filter(item => {
  //     if (!item.permissionKey) {
  //       // console.log(`✅ Navigation item '${item.id}' has no permission key - allowing access`);
  //       return true;
  //     }

  //     const hasAccess = canAccessNavigation(ability as AppAbility, item.permissionKey);
  //     // console.log(`🔍 Navigation item '${item.id}' (${item.permissionKey}):`, hasAccess ? '✅ ALLOWED' : '❌ DENIED');
  //     return hasAccess;
  //   });

  //   // console.log('📋 Final accessible navigation items:', filtered.map(i => i.id));
  //   return filtered;
  // }, [ability, authLoading, isAuthenticated]);

  return (
    <div className="flex flex-col items-center justify-center gap-3">
      {getFilteredMainNavItems().map((item, index) => {
        const isActive = activeMainNav === item.id || location.pathname.startsWith(`/home/${item.id}`);

        return (
          <Protect key={item.id} permission={getPermissionForNavigation(item.permissionKey)} fallback={null}>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      "w-12 h-12 rounded-xl transition-all duration-300 hover:shadow-md relative group",
                      isActive
                        ? "bg-gradient-to-br from-purple-500/20 to-blue-500/20 text-purple-700 shadow-lg"
                        : "text-gray-600 hover:text-purple-600 hover:bg-purple-50/80"
                    )}
                    onClick={() => {
                      setActiveMainNav(item.id);
                      navigate(item.path);
                    }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-100/50 to-blue-100/50 rounded-xl blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <item.icon className="h-5 w-5 relative z-10" />
                    <span className="sr-only">{item.title}</span>
                    {isActive && (
                      <div className="absolute left-0 w-1 h-8 bg-primary rounded-r-full animate-fade-in" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right" className="flex flex-col gap-1">
                  <p className="font-medium">{item.title}</p>
                  <p className="text-xs text-muted-foreground">Alt + {index + 1}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </Protect>
        );
      })}
    </div>
  );
};

export default MainNavigation;